// @ts-nocheck
import React, { Suspense, lazy } from "react";
import {
  BrowserRouter,
  HashRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { SiteHeader } from "./components/SiteHeader.jsx";
import { SiteFooter } from "./components/SiteFooter.jsx";
import { ProjectNavigator } from "./components/ProjectNavigator.jsx";
import { ScrollToTopButton } from "./components/ScrollToTopButton.jsx";
import { ScrollRevealMotion } from "./components/ScrollRevealMotion.jsx";
import { RouteScrollReset } from "./components/RouteScrollReset.jsx";
import { I18nProvider, useI18n } from "./i18n/I18nContext.jsx";
import { HomePage } from "./pages/HomePage.jsx";
import { NotFoundPage } from "./pages/NotFoundPage.jsx";
import { productionGates } from "./config/production-gates.js";
import { Activity } from "lucide-react";
import { DesignSkeletonPage } from "./pages/DesignSkeletonPage.jsx";

const SolutionsHubPage = lazy(() =>
  import("./pages/SolutionsPages.jsx").then((module) => ({
    default: module.SolutionsHubPage,
  })),
);
const SolutionPage = lazy(() =>
  import("./pages/SolutionsPages.jsx").then((module) => ({
    default: module.SolutionPage,
  })),
);
const EquipmentPage = lazy(() =>
  import("./pages/EquipmentPage.jsx").then((module) => ({
    default: module.EquipmentPage,
  })),
);
const EquipmentCategoryPage = lazy(() =>
  import("./pages/EquipmentPage.jsx").then((module) => ({
    default: module.EquipmentCategoryPage,
  })),
);
const EquipmentModelPage = lazy(() =>
  import("./pages/EquipmentPage.jsx").then((module) => ({
    default: module.EquipmentModelPage,
  })),
);
const ManufacturerPage = lazy(() =>
  import("./pages/EquipmentPage.jsx").then((module) => ({
    default: module.ManufacturerPage,
  })),
);
const TracoraPage = lazy(() =>
  import("./pages/SoftwarePages.jsx").then((module) => ({
    default: module.TracoraPage,
  })),
);
const ErpPage = lazy(() =>
  import("./pages/SoftwarePages.jsx").then((module) => ({
    default: module.ErpPage,
  })),
);
const KnowledgeHubPage = lazy(() =>
  import("./pages/KnowledgePages.jsx").then((module) => ({
    default: module.KnowledgeHubPage,
  })),
);
const KnowledgeMaterialPage = lazy(() =>
  import("./pages/KnowledgePages.jsx").then((module) => ({
    default: module.KnowledgeMaterialPage,
  })),
);
const EngineeringCenterPage = lazy(() =>
  import("./pages/EngineeringCenterPage.jsx").then((module) => ({
    default: module.EngineeringCenterPage,
  })),
);
const ServicePage = lazy(() =>
  import("./pages/ServicePage.jsx").then((module) => ({
    default: module.ServicePage,
  })),
);
const ConsumablesPartsPage = lazy(() =>
  import("./pages/ConsumablesPartsPage.jsx").then((module) => ({
    default: module.ConsumablesPartsPage,
  })),
);
const AboutPage = lazy(() =>
  import("./pages/AboutPage.jsx").then((module) => ({
    default: module.AboutPage,
  })),
);
const ContactsPage = lazy(() =>
  import("./pages/ContactsPage.jsx").then((module) => ({
    default: module.ContactsPage,
  })),
);
const PrivacyPage = lazy(() =>
  import("./pages/PrivacyPage.jsx").then((module) => ({
    default: module.PrivacyPage,
  })),
);

function AppLayout() {
  const location = useLocation();
  const { t } = useI18n();
  if (location.pathname === "/design-skeleton") {
    return <DesignSkeletonPage />;
  }
  return (
    <>
      <a className="skip-link" href="#main-content">
        {t.skip}
      </a>
      <SiteHeader />
      <RouteScrollReset />
      <div id="main-content" tabIndex={-1}>
        <Suspense
          fallback={
            <div className="route-loading" role="status" aria-live="polite">
              <div className="route-loading-mark">
                <Activity aria-hidden="true" />
                <span>ANLEXX</span>
              </div>
              <div className="route-loading-lines">
                <i />
                <i />
                <i />
              </div>
              <small>{t.loading}</small>
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/solutions" element={<SolutionsHubPage />} />
            <Route path="/solutions/:slug" element={<SolutionPage />} />
            <Route path="/equipment" element={<EquipmentPage />} />
            <Route
              path="/equipment/:category"
              element={<EquipmentCategoryPage />}
            />
            <Route
              path="/equipment/:category/:product"
              element={<EquipmentModelPage />}
            />
            <Route path="/manufacturers/:slug" element={<ManufacturerPage />} />
            <Route path="/software/tracora" element={<TracoraPage />} />
            <Route path="/software/anlexx-erp" element={<ErpPage />} />
            <Route path="/service" element={<ServicePage />} />
            <Route
              path="/consumables-parts"
              element={<ConsumablesPartsPage />}
            />
            <Route
              path="/engineering-center"
              element={<EngineeringCenterPage />}
            />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route
              path="/knowledge"
              element={
                productionGates.knowledge ? (
                  <KnowledgeHubPage />
                ) : (
                  <Navigate to="/not-found" replace />
                )
              }
            />
            <Route
              path="/knowledge/:slug"
              element={
                productionGates.knowledge ? (
                  <KnowledgeMaterialPage />
                ) : (
                  <Navigate to="/not-found" replace />
                )
              }
            />
            <Route path="/not-found" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </div>
      <ScrollRevealMotion />
      <ScrollToTopButton />
      <ProjectNavigator />
      <SiteFooter />
    </>
  );
}

export function App() {
  const Router =
    window.location.protocol === "file:" ? HashRouter : BrowserRouter;
  return (
    <Router>
      <I18nProvider>
        <AppLayout />
      </I18nProvider>
    </Router>
  );
}
